import { DateRange } from "react-day-picker";

import { supabase } from "~/integrations/supabase/client";
import { resolveGross } from "~/utils/iva";

// Type for status summary data returned from database
export interface StatusSummaryData {
  count: number;
  total_amount: number;
  price_with_iva: number;
}

// Type for the complete status summary response
export type StatusSummaryResponse = Record<string, StatusSummaryData>;

// Interface for the service parameters
interface FetchStatusSummaryParams {
  dateRange: DateRange;
  userRole?: string;
  userId?: string;
  viewAllSellers?: boolean;
}

/**
 * Fetches the "facturado" virtual status totals (in_transit without assigned_transportista)
 */
const fetchFacturadoTotals = async ({
  dateRange,
  userRole,
  userId,
  viewAllSellers,
}: FetchStatusSummaryParams): Promise<StatusSummaryData> => {
  const startDate = dateRange.from!.toISOString();
  const endDate = dateRange.to!.toISOString();

  let query = supabase
    .from("sales_request")
    .select("total_amount, price_with_iva, is_exento")
    .eq("status", "in_transit")
    .is("assigned_transportista", null)
    .is("deleted_at", null)
    // Exclude no-venta (muestra / traslado) orders: they are not sales.
    .not("is_non_sale", "is", true)
    .gte("created_at", startDate)
    .lte("created_at", endDate);

  // Apply role-based filters
  if (userRole === "seller" && userId && !viewAllSellers) {
    query = query.eq("created_by", userId);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching facturado totals:", error);
    return { count: 0, total_amount: 0, price_with_iva: 0 };
  }

  // Calculate totals from the results
  const totals = (data || []).reduce(
    (acc, order) => ({
      count: acc.count + 1,
      total_amount: acc.total_amount + (order.total_amount || 0),
      price_with_iva: acc.price_with_iva + resolveGross(order),
    }),
    { count: 0, total_amount: 0, price_with_iva: 0 }
  );

  return totals;
};

/**
 * Fetches status summary data (counts and amounts) from the database
 * Uses the get_sales_request_status_summary database function for optimal performance
 */
export const fetchStatusSummary = async ({
  dateRange,
  userRole,
  userId,
  viewAllSellers = false,
}: FetchStatusSummaryParams): Promise<StatusSummaryResponse> => {
  try {
    // Validate date range
    if (!dateRange?.from || !dateRange?.to) {
      console.warn("Invalid date range provided to fetchStatusSummary");
      return {};
    }

    // Format dates for the database function
    const startDate = dateRange.from.toISOString().split("T")[0];
    const endDate = dateRange.to.toISOString().split("T")[0];

    // Call the database function and fetch facturado totals in parallel
    const [rpcResult, facturadoTotals] = await Promise.all([
      supabase.rpc("get_sales_request_status_summary", {
        start_date: startDate,
        end_date: endDate,
        user_id: userId || null,
        user_role: userRole || null,
        view_all_sellers: viewAllSellers,
      }),
      fetchFacturadoTotals({ dateRange, userRole, userId, viewAllSellers }),
    ]);

    if (rpcResult.error) {
      console.error("Error fetching status summary:", rpcResult.error);
      throw rpcResult.error;
    }

    const data = rpcResult.data || {};

    // Add the "facturado" virtual status to the response
    data.facturado = facturadoTotals;

    return data;
  } catch (error) {
    console.error("Failed to fetch status summary:", error);
    throw error;
  }
};

/**
 * Helper function to get the total count across all statuses
 */
export const getTotalCount = (statusSummary: StatusSummaryResponse): number => {
  return Object.values(statusSummary).reduce(
    (total, status) => total + status.count,
    0
  );
};

/**
 * Helper function to get the total amount across all statuses
 */
export const getTotalAmount = (
  statusSummary: StatusSummaryResponse
): number => {
  return Object.values(statusSummary).reduce(
    (total, status) => total + status.total_amount,
    0
  );
};

/**
 * Helper function to get the total price with IVA across all statuses
 */
export const getTotalPriceWithIva = (
  statusSummary: StatusSummaryResponse
): number => {
  return Object.values(statusSummary).reduce(
    (total, status) => total + status.price_with_iva,
    0
  );
};
